import React from 'react';
import { useFormikContext } from 'formik';

import AdjusterSelectTextFieldFormik from '~/Adjuster/AdjusterSelectTextFieldFormik';
import { useStyles } from '~/assets/styles';
import { getAllOrganizationWideRoles } from '~/components/communications/ContactUtils';
import ContactSearchContainerOutOfClaim from '~/components/ContactSearch/ContactSearchContainerOutOfClaim';
import { ErrorHelperTextFormik } from '~/components/core/Formik/ErrorHelperTextFormik';
import type {
  MoiAutomaticRulesAssignee,
  MoiFormikValues,
  MoiStepProps,
} from '~/components/exposures/moi/MoiStepper/types';
import VendorContactSearchLabel from '~/components/exposures/moi/VendorContactSearchLabel';
import useOrganization from '~/components/OrganizationContext';
import type { UserModel } from '~/components/types/user-types';

import AutomaticRulesAssigneeField from './AutomaticRulesAssigneeField';
import useSelectVendorContact from './useSelectVendorContact';

interface AssigneeSelectFieldProps extends MoiStepProps {
  automaticRulesAssignee?: MoiAutomaticRulesAssignee | null;
}

const AssigneeSelectField: React.FC<AssigneeSelectFieldProps> = ({
  setSelectedContact,
  getIsContactVendorApi,
  setIsXactContractor,
  setIsVendorApi,
  selectedContact,
  moiMethodsReturn,
  automaticRulesAssignee,
}) => {
  const { values, setFieldValue, isSubmitting } = useFormikContext<MoiFormikValues>();
  const { organizationContactRolesDict }: { organizationContactRolesDict?: { [key: string]: unknown } } =
    useOrganization();
  const { getMoiMethodExpertises } = moiMethodsReturn;
  const { classes } = useStyles();
  const selectVendorContact = useSelectVendorContact({
    getIsContactVendorApi,
    setIsVendorApi,
    setIsXactContractor,
    setSelectedContact,
    moiMethodsReturn,
  });

  return (
    <div className="col-span-6 col-start-1 pt-4">
      <>
        {values['assignee_type'] === 'service_provider' && (
          <>
            <ContactSearchContainerOutOfClaim
              fixedSearchResults
              onSelectContact={selectVendorContact}
              selectedContactId={selectedContact ? selectedContact.id : undefined}
              selectedContactDisplayName={selectedContact ? selectedContact.full_name : undefined}
              TextFieldProps={{
                label: <VendorContactSearchLabel disabled={false} />,
                disabled: !values['moi_method_id'],
              }}
              acceptedRoles={getAllOrganizationWideRoles(organizationContactRolesDict)}
              acceptedExpertises={getMoiMethodExpertises(values['moi_method_id'])}
              restrictAcceptedEntities
              textInputRef={undefined}
              overrideOfContactsContextSearchOrganizationId={undefined}
            />
            <ErrorHelperTextFormik className="mt-2" id="contact_id" />
          </>
        )}
        {values['assignee_type'] === 'automatic_rules' && (
          <AutomaticRulesAssigneeField automaticRulesAssignee={automaticRulesAssignee} />
        )}
        {values['assignee_type'] === 'internal_user' && (
          <>
            <AdjusterSelectTextFieldFormik
              id="user_id"
              label="Internal User"
              className={classes.textField}
              onChange={(_: unknown, adjuster: UserModel) => {
                setFieldValue('user_id', adjuster ? adjuster.id : '');
              }}
              fullWidth
              disabled={isSubmitting || !values['moi_method_id']}
              currentAdjusterMark=" (Assign to me)"
              sortAlphabetic
              includeDaSpecialist={undefined}
              excludedAdjustersIds={undefined}
              allowedAdjustersIds={undefined}
              showOnly={undefined}
              extraValues={undefined}
              allUsers={undefined}
              checkLicenses={undefined}
            />
          </>
        )}
      </>
    </div>
  );
};

export default AssigneeSelectField;
